import clsx from "clsx";
import { useFormik } from 'formik';
import { uniqueId } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { IEvento } from "../../interfaces/IEvento";
import { IGrupoEvento } from "../../interfaces/IGrupoEvento";
import { IRegimeTributarioEvento } from "../../interfaces/IRegimeTributarioEvento";
import { IRetencaoFonte } from "../../interfaces/IRetencaoFonte";
import GeracaoCreditoService from "../../services/GeracaoCreditoService";
import GrupoEventoService from "../../services/GrupoEventoService";
import ObrigacaoAcessoriasService from "../../services/ObrigacaoAcessoriasService";
import PeriodicidadeService from "../../services/PeriodicidadeService";
import RetencaoFonteService from "../../services/RetencaoFonteService";
import { ListaMarcacaoRegimeTributario } from "./Evento";

interface EventoModalProps {
    toggleModal: () => void,
    salvar: (evento: IEvento) => void,
    show: boolean,
    evento?: IEvento,
    title: string
}

function EventoModal({ toggleModal, salvar, show, evento, title }: EventoModalProps) {

    const [todosMarcados, setTodosMarcados] = useState<boolean>(false);
    const [grupoEventos, setGrupoEventos] = useState<IGrupoEvento[]>([]);
    const [retencoesFonte, setRetencoesFonte] = useState<IRetencaoFonte[]>([]);
    const [periodicidadesRecolhimento, setPeriodicidadesRecolhimento] = useState<any[]>([]);
    const [geracoesCredito, setGeracoesCredito] = useState<any[]>([]);
    const [obrigacoesAcessoria, setObrigacoesAcessoria] = useState<any[]>([]);
    const [listaObrigacoes, setListaObrigacoes] = useState<any[]>([]);
    const [obrigacoesSelecionadas, setObrigacoesSelecionadas] = useState<any[]>([]);
    const [regimesTributarioId, setRegimesTributarioId] = useState<number[]>([]);
    const eventosSchema = Yup.object().shape({
        nome: Yup.string().required('Nome é obrigatório').max(100, 'Máximo de 100 Caracteres'),
        contaReferencial: Yup.string().required('Conta referencia é obrigatório').max(20, 'Máximo de 100 Caracteres'),
        descContaReferencial: Yup.string().required('Descrição de Conta referencia é obrigatório').max(150, 'Máximo de 100 Caracteres')
    });
    const obrigacaoSchema = Yup.object().shape({
        obrigacaoAcessoriaID: Yup.string().required('Por favor selecione uma opção'),
    });
    const formInitialValues: IEvento = {
        id: 0,
        nome: '',
        geracaoCreditoID: 0,
        periodicidadeID: 0,
        obrigacaoAcessoriaID: 0,
        contaReferencial: 0,
        descContaReferencial: '',
        grupoEventoID: 0,
        isFornecedor: false,
        isFolha: false,
        isCapitalSocial: false,
        isLucrosAcumulados: false,
        isPrejuizosAcumulados: false,
        isCapitalSubscrever: false,
        isSalario: false,
        isTributo: false,
        isEstoque: false,
        isBanco: false,
        isDepreciado: false,
        isImobilizado: false,
        isCaixa: false,
        tipoRetencaoFonteID: 0,
        regimesTributarios: [],
        obrigacoesAcessorias: []
    };

    const formInitialValuesObrigacoesAcessorias: any = {
        obrigacaoAcessoriaID: 0
    }
    
    const formik = useFormik({
        initialValues: formInitialValues,
        validationSchema: eventosSchema,
        onSubmit: (values, { setSubmitting }) => {
            values.obrigacoesAcessorias = obrigacoesSelecionadas
            salvar(values);
            setSubmitting(false);
        }
    });

    const formikObrigacoesAcessorias = useFormik({
        initialValues: formInitialValuesObrigacoesAcessorias,
        validationSchema: obrigacaoSchema,
        onSubmit: (values, { setSubmitting }) => {
            const filter = obrigacoesAcessoria.find((x) => x.id === parseInt(values.obrigacaoAcessoriaID));
            if (filter) {
                let filtered: any[] = [];
                setObrigacoesSelecionadas((oldValues) => {
                    const newValue = oldValues;
                    newValue.push(filter);
                    filtered = obrigacoesAcessoria.filter((x) => newValue.filter(p => p.id == x.id).length === 0);
                    return newValue;
                });

                setListaObrigacoes(filtered);
                formikObrigacoesAcessorias.resetForm();
            }
        }
    });

    useEffect(() => {
        formik.resetForm();

        if (evento) {
            setObrigacoesSelecionadas(evento.obrigacoesAcessorias);
            formik.setValues(evento);

            !evento.nome && formik.setFieldTouched('nome', false);
            !evento.contaReferencial && formik.setFieldTouched('contaReferencial', false);
            !evento.descContaReferencial && formik.setFieldTouched('descContaReferencial', false);
            // !evento.regimesTributarios && formik.setFieldTouched('regimesTributarios', false);
            !evento.isTributo && formik.setFieldTouched('isTributo', false);
        }

        inicializar();

        // eslint-disable-next-line
    }, [evento]);

    useEffect(() => {
        setTodosMarcados(regimesTributarioId.length === ListaMarcacaoRegimeTributario.length - 1);

        formik.setFieldValue('regimesTributarios', regimesTributarioId.map(x => {
            const regimeTributario: IRegimeTributarioEvento = {
                regimeTributarioID: x
            };

            return regimeTributario;
        }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regimesTributarioId]);

    useEffect(() => {
        const filtered = obrigacoesAcessoria.filter((x) => obrigacoesSelecionadas.filter(p => p.id == x.id).length === 0);
        setListaObrigacoes(filtered);
    }, [obrigacoesSelecionadas, obrigacoesAcessoria]);

    async function inicializar() {
        const array = (evento?.regimesTributarios ?? []).map(x => x.regimeTributarioID ? x.regimeTributarioID : -1);

        setRegimesTributarioId(array);
        setGrupoEventos(await GrupoEventoService.obterTodos());
        setPeriodicidadesRecolhimento(await PeriodicidadeService.obterTodos());
        setGeracoesCredito(await GeracaoCreditoService.obterTodos());

        await ObrigacaoAcessoriasService.obterTodos(x => setObrigacoesAcessoria(x));
        await RetencaoFonteService.obterTodosTipos(x => setRetencoesFonte(x));
    }

    async function trocarValor(id: number, valor: boolean) {
        if (id === 0) {
            if (valor) {
                setRegimesTributarioId(ListaMarcacaoRegimeTributario.filter(x => x.id !== 0).map(x => x.id));
            }
            else {
                setRegimesTributarioId([]);
            }
        }
        else {
            if (valor) {
                setRegimesTributarioId([...regimesTributarioId, id]);
            }
            else {
                setRegimesTributarioId(regimesTributarioId.filter(x => x !== id));
            }
        }
    }

    const removerObrigacaoVinculada = useCallback((index: number) => {
        setObrigacoesSelecionadas((old) => {
            console.log(index, old)
            const result = obrigacoesSelecionadas.findIndex(p => p.id === index);
            return old.splice(result, 1);
        });
    }, [obrigacoesSelecionadas]);

    return <Modal size='lg' centered={true} show={show} onHide={toggleModal}>
        <div className="modal-content">
            <div className="modal-header">
                <h5 className="modal-title titulo-modal">{title}</h5>
                <div className="btn btn-icon btn-sm btn-active-light-primary ms-2">
                    <i onClick={toggleModal} className="fas fa-times" style={{ color: '#5E6397' }}></i>
                </div>
            </div>
            <div className="modal-body">
                <form className="row g-3">
                    <div className="col-md-8">
                        <label htmlFor="form-evento-nomedoevento" className="form-label required">Nome do Evento</label>
                        <input {...formik.getFieldProps('nome')} placeholder="ISS" type="text" className={clsx(
                            'form-control',
                            {
                                'is-invalid': formik.touched.nome && formik.errors.nome
                            },
                            {
                                'is-valid': formik.touched.nome && !formik.errors.nome
                            }
                        )} id="form-evento-nomedoevento" />
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="form-evento-grupodoevento" className="form-label required">Grupo do Evento</label>
                        <select id="form-evento-grupodoevento"
                            {...formik.getFieldProps('grupoEventoID')}
                            onChange={async e => await formik.setFieldValue(e.target.name, e.target.value)} className="form-select">

                            <option value="" hidden>Selecione</option>
                            {
                                grupoEventos.length > 0 && grupoEventos.sort((a, b) => a.id > b.id ? 1 : -1).map(op => <option key={op.id} value={op.id}>{op.nome}</option>)
                            }
                        </select>
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="form-evento-contareferencial" className="form-label required mt-5">Conta Referencial</label>
                        <input  {...formik.getFieldProps('contaReferencial')} placeholder="0.0.0.0.00.00" type="email" className={clsx(
                            'form-control',
                            {
                                'is-invalid': formik.touched.contaReferencial && formik.errors.contaReferencial,
                            },
                            {
                                'is-valid': formik.touched.contaReferencial && !formik.errors.contaReferencial,
                            }
                        )} id="form-evento-nomedoevento" />
                    </div>
                    <div className="col-md-8">
                        <label htmlFor="form-evento-contarefencial" className="form-label required mt-5">Descrição da conta referencial</label>
                        <input {...formik.getFieldProps('descContaReferencial')} placeholder="Digite Algo" type="email" className={clsx(
                            'form-control',
                            {
                                'is-invalid': formik.touched.descContaReferencial && formik.errors.descContaReferencial,
                            },
                            {
                                'is-valid': formik.touched.descContaReferencial && !formik.errors.descContaReferencial,
                            }
                        )} id="form-evento-nomedoevento" />
                    </div>
                    <div className="col-md-5">
                        <label htmlFor="form-evento-retencaonafonte" className="form-label required mt-5">Retenção na fonte</label>
                        <select id="form-evento-retencaonafonte" {...formik.getFieldProps('tipoRetencaoFonteID')}
                            onChange={async e => await formik.setFieldValue(e.target.name, e.target.value)} className="form-select">

                            <option value="" hidden>Selecione</option>
                            {
                                retencoesFonte.length > 0 && retencoesFonte.map((x, index) => <option key={index} value={x.id}>{x.descricao}</option>)
                            }
                        </select>
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="form-evento-periodicidade" className="form-label required mt-5">Períodicidade do recolhimento</label>
                        <select id="form-evento-periodicidade" {...formik.getFieldProps('periodicidadeID')}
                            onChange={async e => await formik.setFieldValue(e.target.name, e.target.value)} className="form-select" placeholder="Digite Algo">

                            <option value="" hidden>Selecione</option>
                            {
                                periodicidadesRecolhimento.length > 0 && periodicidadesRecolhimento.map((x, index) => <option key={index} value={x.id}>{x.nome}</option>)
                            }
                        </select>
                    </div>

                    <div className="col-md-5">
                        <label htmlFor="form-evento-geracaodecredito" className="form-label required mt-5">Geração de crédito</label>
                        <select id="form-evento-geracaodecredito" {...formik.getFieldProps('geracaoCreditoID')}
                            onChange={async e => await formik.setFieldValue(e.target.name, e.target.value)} className="form-select">

                            <option value="" hidden>Selecione</option>
                            {
                                geracoesCredito.length > 0 && geracoesCredito.map((x, index) => <option key={index} value={x.id}>{x.nome}</option>)
                            }
                        </select>
                    </div>
                    <div className="col-md-12">
                        <div className="row">
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-istributo"
                                        checked={formik.values.isTributo} onChange={async e => await formik.setFieldValue('isTributo', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-istributo">É Tributo</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isBanco"
                                        checked={formik.values.isBanco} onChange={async e => await formik.setFieldValue('isBanco', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isBanco">É Banco</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isCaixa"
                                        checked={formik.values.isCaixa} onChange={async e => await formik.setFieldValue('isCaixa', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isCaixa">É Caixa</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isImobilizado"
                                        checked={formik.values.isImobilizado} onChange={async e => await formik.setFieldValue('isImobilizado', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isImobilizado">É Imobilizado</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isDepreciado"
                                        checked={formik.values.isDepreciado} onChange={async e => await formik.setFieldValue('isDepreciado', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isDepreciado">É Depreciação</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isEstoque"
                                        checked={formik.values.isEstoque} onChange={async e => await formik.setFieldValue('isEstoque', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isEstoque">É Estoque</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isFornecedor"
                                        checked={formik.values.isFornecedor} onChange={async e => await formik.setFieldValue('isFornecedor', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isFornecedor">É Fornecedor/Cliente</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isFolha"
                                        checked={formik.values.isFolha} onChange={async e => await formik.setFieldValue('isFolha', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isFolha">É Provisão da Folha</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isCapitalSocial"
                                        checked={formik.values.isCapitalSocial} onChange={async e => await formik.setFieldValue('isCapitalSocial', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isCapitalSocial">É Capital Social</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isLucrosAcumulados"
                                        checked={formik.values.isLucrosAcumulados} onChange={async e => await formik.setFieldValue('isLucrosAcumulados', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isLucrosAcumulados">É Lucros Acumulados</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isPrejuizosAcumulados"
                                        checked={formik.values.isPrejuizosAcumulados} onChange={async e => await formik.setFieldValue('isPrejuizosAcumulados', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isPrejuizosAcumulados">É Prejuizos Acumulados</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isCapitalSubscrever"
                                        checked={formik.values.isCapitalSubscrever} onChange={async e => await formik.setFieldValue('isCapitalSubscrever', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isCapitalSubscrever">É Capital Subscrever</label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-5">
                                    <input className="form-check-input" type="checkbox" id="form-regime-isSalario"
                                        checked={formik.values.isSalario} onChange={async e => await formik.setFieldValue('isSalario', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="form-regime-isSalario">É Salário</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <label htmlFor="form-evento-regimentotributario" className="form-label required mt-5">Regimento Tributário</label>
                    <div className="col-12" style={{ display: 'grid', columns: '2' }}>
                        {
                            ListaMarcacaoRegimeTributario.map((x, i) => {
                                const index = i + 1;
                                const colunas = 2; //ainda não responsivo para mais colunas
                                const quantidadePorColuna = Math.round(ListaMarcacaoRegimeTributario.length / colunas);
                                const posicaoLinha = index > quantidadePorColuna ? index - quantidadePorColuna : index;
                                const posicaoColuna = index >= colunas ? Math.ceil(index / quantidadePorColuna) : index;

                                return <div key={index} className="form-check mt-2" style={{ gridColumn: posicaoColuna, gridRow: posicaoLinha }}>
                                    <input className="form-check-input" type="checkbox" checked={x.id === 0 ? todosMarcados : regimesTributarioId.find(p => p === x.id) !== undefined} onChange={e => trocarValor(x.id, e.target.checked)} id={`form-regime-tributario-${x.id}`} />
                                    <label className="form-check-label" htmlFor={`form-regime-tributario-${x.id}`}>
                                        {x.nome}
                                    </label>
                                </div>
                            })
                        }
                    </div>
                </form>
                <form className="row g-3 mt-5">
                    <div className="col-md-12">
                        <h3>Obrigações Acessórias</h3>
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="form-evento-obrigacaoacessoria" className="form-label required mt-5">Lista de Obrigação Acessória</label>
                        <select id="form-evento-obrigacaoacessoria" {...formikObrigacoesAcessorias.getFieldProps('obrigacaoAcessoriaID')}
                            onChange={async e => await formikObrigacoesAcessorias.setFieldValue(e.target.name, e.target.value)} className="form-select">

                            <option value="" hidden>Selecione</option>
                            {
                                listaObrigacoes.length > 0 && listaObrigacoes.map((x, index) => <option key={index} value={x.id}>{x.descricao}</option>)
                            }
                        </select>
                    </div>
                    <div className="col-md-2 mt-16">
                        <button  disabled={formik.isSubmitting} onClick={() => formikObrigacoesAcessorias.submitForm()} type="button" className="btn btn-primario ms-5">Inserir</button>
                    </div>
                    <div className="col-md-12">
                        <table className="table">
                            <thead>
                                <tr>
                                    <td>Nome</td>
                                    <td></td>
                                </tr>
                            </thead>
                            <tbody>
                                {obrigacoesSelecionadas.map((item) => (    
                                    <tr key={uniqueId()}>
                                        <td style={{ width: '216px', fontSize: '18px' }}>{item.descricao}</td>
                                        <td><button onClick={() => removerObrigacaoVinculada(item.id)} style={{ width: '100px', height: '47px' }} type="button" className="btn btn-modal-cancelar me-5">Remover</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </form>
            </div>
            <div className="modal-footer" style={{ margin: '0 auto' }}>
                <button disabled={formik.isSubmitting} onClick={() => toggleModal()} type="button" className="btn btn-modal-cancelar me-5"> CANCELAR </button>
                <button disabled={formik.isSubmitting || !formik.isValid} onClick={() => formik.submitForm()} type="button" className="btn btn-primario ms-5">
                    {
                        !formik.isSubmitting && (evento?.id ? 'EDITAR' : 'ADICIONAR')
                    }
                    {
                        formik.isSubmitting &&
                        <span className='indicator-progress' style={{ display: 'block' }}>
                            Salvando...
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                    }
                </button>
            </div>
        </div>
    </Modal>
}

export default EventoModal;